/*
 * Whether this visit has finished the sign-up (/start), so the rest of the
 * site opens. App.js guards every route but Home and /start with it, typed
 * URLs included, and on a configured build also checks that Firebase really
 * holds a verified visitor (signupService.isVerified), so a hand-made flag
 * does not get anyone in.
 *
 * sessionStorage['ng.access'] = {"v":2,"at":<ms>}: this tab's visit only, so
 * every new visit meets the form again (client, 2026-10-21: "be strict that
 * nobody can bypass the form"). Written only once the code is verified and
 * the confirm step finishes (src/pages/start/SignUp.jsx); there is no other
 * way in. It also lapses after ACCESS_HOURS; an expired, future-dated or
 * garbled value counts as not signed up. The old 30-day localStorage copy is
 * removed on load.
 *
 * Storage can be unavailable (private mode, blocked site data). Reading then
 * fails closed (not signed up), and a verified visitor is kept in memory for
 * the rest of this page's life instead, so they are never let in for free and
 * never sent back to the form mid-visit.
 */
const STORAGE_KEY = 'ng.access';
const VERSION = 2;
export const ACCESS_HOURS = 12;
const ACCESS_MS = ACCESS_HOURS * 60 * 60 * 1000;

// Set only when storage refused the write.
let grantedInMemoryAt = null;

// Bumped on every grant and revoke, so the guard's Firebase check (App.js)
// runs again for each new sign-in instead of once per page load.
let generation = 0;
export const accessGeneration = () => generation;

// The earlier 30-day flag lived in localStorage: it no longer counts.
try {
  window.localStorage.removeItem(STORAGE_KEY);
} catch {
  // Storage unavailable: nothing stored to remove.
}

const isFresh = (at, now) => Number.isFinite(at) && at <= now && now - at < ACCESS_MS;

export function hasAccess(now = Date.now()) {
  if (grantedInMemoryAt !== null && isFresh(grantedInMemoryAt, now)) return true;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));
    return Boolean(parsed) && parsed.v === VERSION && isFresh(parsed.at, now);
  } catch {
    return false;
  }
}

export function grantAccess(now = Date.now()) {
  generation += 1;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ v: VERSION, at: now }));
    grantedInMemoryAt = null;
  } catch {
    grantedInMemoryAt = now;
  }
}

// Forgets the sign-up on this browser (both copies): Log out, and the tests.
export function revokeAccess() {
  generation += 1;
  grantedInMemoryAt = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing stored to remove.
  }
}

/*
 * Where /start sends the visitor once they finish: the `next` the guard
 * passed along, when it is a path on this site ("/…", not "//…" or "/\…",
 * which browsers read as another host, and not /start itself), else Home.
 */
export function safeNextPath(next) {
  if (typeof next !== 'string' || !next.startsWith('/')) return '/';
  if (next.startsWith('//') || next.startsWith('/\\')) return '/';
  // Control characters and whitespace never belong in an in-app path.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\s]/.test(next)) return '/';
  if (/^\/start(?:[/?#]|$)/.test(next)) return '/';
  return next;
}
